import { keepPreviousData } from '@tanstack/react-query';
import { useState } from 'react';
import { useDebounce } from 'use-debounce';

import { Button } from '@/components/ui/button';
import { FilterDropdown } from '@/components/filters/FilterDropdown';
import { NameColumn } from '@/components/NameColumn';
import { getColumnWidth } from '@/components/table/columns';
import { Table } from '@/components/table/Table';
import { TableControlsContainer } from '@/components/table/TableControlsContainer';
import { ColumnPriority, Columns } from '@/components/table/types';
import { ClearFiltersButton } from '@/components/legacy-ui/ClearFiltersButton';
import { SearchBar } from '@/components/legacy-ui/SearchBar';
import { useGetTechnologyFilters } from '@/hooks/api/useGetTechnologyFilters';
import { useMy } from '@/hooks/useMy';
import type { Filter } from '@/hooks/useTechnology';
import { VendorCell } from '@/sections/technology/components/cells';
import { getFilterDropdownWidth } from '@/sections/technology/config';
import { parseTechnologyVersion } from '@/sections/technology/utils';
import { useGlobalState } from '@/state/global.state';
import type { NodeDetails, Relationship, Technology } from '@/types';
import { getTechnologyDisplayProduct } from '@/utils/entities/technology.util';
import { omit } from '@/utils/lodash.util';

import { FindingsCountCell } from './cells/FindingsCountCell';

const osintColumns: Columns<Technology> = [
  {
    label: 'Vendor',
    id: 'vendor',
    ...getColumnWidth('technology', 'vendor'),
    copy: true,
    cell: (data: Technology) => <VendorCell entity={data} />,
    sorting: true,
    responsive: { priority: ColumnPriority.CRITICAL, alwaysVisible: true },
  },
  {
    label: 'Technology',
    id: 'name',
    ...getColumnWidth('technology', 'name'),
    cell: (data: Technology) => (
      <NameColumn
        name={getTechnologyDisplayProduct(data)}
        description={
          data.version ? parseTechnologyVersion(data.version, true) : undefined
        }
      />
    ),
    sorting: true,
    responsive: { priority: ColumnPriority.CRITICAL, alwaysVisible: true },
  },
  {
    label: 'Findings',
    id: '',
    fixedWidth: 100,
    cell: () => <FindingsCountCell count={undefined} />,
    sorting: false,
    responsive: { priority: ColumnPriority.HIGH },
  },
  {
    label: 'First Seen',
    id: 'created',
    ...getColumnWidth('technology', 'created'),
    copy: true,
    cell: 'date',
    sorting: true,
    responsive: { priority: ColumnPriority.MEDIUM },
  },
  {
    label: 'Last Seen',
    id: 'visited',
    ...getColumnWidth('technology', 'visited'),
    copy: true,
    cell: 'date',
    sorting: true,
    responsive: { priority: ColumnPriority.HIGH },
  },
];

interface OsintResearchTableProps {
  onResearchClick: () => void;
}

export function OsintResearchTable({
  onResearchClick,
}: OsintResearchTableProps) {
  const { drawerState } = useGlobalState();
  const [filter, setFilter] = useState<Filter>({});
  const { data: propertyFilters, status: propertyFiltersStatus } =
    useGetTechnologyFilters();

  const [debouncedSearch] = useDebounce(
    filter.search || '',
    filter.search === '' || !filter.search ? 0 : 300
  );

  const nodeFilters: NonNullable<NodeDetails['filters']> = [];
  if (filter.properties) {
    Object.entries(filter.properties).forEach(([key, value]) => {
      if (value && value.length > 0) {
        nodeFilters.push({ field: key, operator: '=', value });
      }
    });
  }

  const {
    data: technologies,
    status,
    error,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useMy(
    {
      resource: 'technology',
      query: {
        node: {
          labels: ['Technology'],
          filters: nodeFilters,
          ...(debouncedSearch && debouncedSearch.length >= 2
            ? { search: debouncedSearch }
            : {}),
          relationships: [
            {
              label: 'HAS_REPOSITORY' as Relationship,
              target: {
                labels: ['Repository'],
              },
            },
          ],
        },
      },
    },
    {
      placeholderData: keepPreviousData,
    }
  );

  // Only show vendor, product, and version filters (no tags in OSINT view)
  const osintFilterKeys = ['vendor', 'product', 'version'] as const;

  return (
    <>
      <TableControlsContainer
        filters={
          <>
            {osintFilterKeys.map(key => {
              const options = propertyFilters[key] || [];
              return (
                <FilterDropdown
                  key={key}
                  id={key}
                  isLoading={propertyFiltersStatus === 'pending'}
                  label={key.charAt(0).toUpperCase() + key.slice(1)}
                  options={options}
                  selectedValues={filter?.properties?.[key] || []}
                  onApply={selected => {
                    setFilter(prev => ({
                      ...prev,
                      properties:
                        selected.length > 0
                          ? {
                              ...prev?.properties,
                              [key]: selected.map(
                                option => option.value as string
                              ),
                            }
                          : omit(prev?.properties, key),
                    }));
                  }}
                  onClear={() => {
                    setFilter(prev => ({
                      ...prev,
                      properties: omit(prev?.properties, key),
                    }));
                  }}
                  width={getFilterDropdownWidth(key)}
                />
              );
            })}
            {filter?.properties &&
              Object.keys(filter.properties).length > 0 && (
                <ClearFiltersButton
                  onClick={() =>
                    setFilter(prev => ({ ...prev, properties: {} }))
                  }
                />
              )}
          </>
        }
        selectionBar={null}
        filtersRight={
          <SearchBar
            size="sm"
            className="h-fit w-64"
            placeholder="Search technologies"
            value={filter.search}
            onChange={value => setFilter(prev => ({ ...prev, search: value }))}
          />
        }
      />
      <Table<Technology>
        name="osint-constantine-targets"
        columns={osintColumns}
        data={technologies}
        status={status}
        error={error}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        keyboardNavigation
        onRowClick={technology =>
          drawerState.handleOpenTechnologyDrawer(technology, true)
        }
        noData={{
          title: 'No OSINT Research Targets Yet',
          description:
            'Use the "Research with Constantine" button to add open-source technologies for zero-day vulnerability research.',
          action: (
            <Button
              onClick={onResearchClick}
              className="rounded-full"
              size="sm"
            >Research with Constantine</Button>
          ),
        }}
      />
    </>
  );
}
